import { useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IterpecHotel, IterpecHotelRoom } from "@/types/iterpec";
import { HotelManualInfo } from "@/hooks/useHotelManualInfo";
import { useHotelManualFaq } from "@/hooks/useHotelManualFaq";
import { AmenityCategory } from "@/utils/hotelAmenities";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  Clock, 
  MapPin, 
  UtensilsCrossed, 
  Bed, 
  Shield, 
  PawPrint,
  Wifi,
  Car,
  Waves,
  Phone,
  HelpCircle,
  MessageSquare,
  ChevronDown
} from "lucide-react";

interface HotelFAQProps {
  hotel: IterpecHotel;
  rooms: IterpecHotelRoom[];
  manualInfo: HotelManualInfo | null;
  amenityCategories: AmenityCategory[];
  onFaqItemsChange?: (items: Array<{ question: string; answer: string }>) => void;
}

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  icon: React.ReactNode;
  category: string;
  isManual?: boolean;
}

export const HotelFAQ = ({ hotel, rooms, manualInfo, amenityCategories, onFaqItemsChange }: HotelFAQProps) => {
  const { t } = useTranslation();
  const { faqItems: manualFaqItems, getLocalizedQuestion, getLocalizedAnswer } = useHotelManualFaq(hotel?.HotelId);

  // Extract hotel name without location suffix for cleaner FAQ text
  const hotelName = useMemo(() => {
    const fullName = hotel?.Name || '';
    // Remove location suffix like "(City, State, Country)" if present
    return fullName.replace(/\s*\([^)]+\)\s*$/, '').trim();
  }, [hotel?.Name]);

  // Auto-generated FAQ items
  const autoFaqItems = useMemo(() => {
    const items: FAQItem[] = [];

    // Destination
    const destinationName = [hotel.City, hotel.Country].filter(Boolean).join(', ');
    if (destinationName) {
      items.push({
        id: "destination",
        category: t('hotelFaq.categories.location'),
        question: t('hotelFaq.destination.question', { hotelName }),
        answer: t('hotelFaq.destination.answer', { hotelName, destinationName }),
        icon: <MapPin className="h-5 w-5" />,
      });
    }

    // Check-in time
    const checkInHour = rooms[0]?.CheckInHour;
    items.push({
      id: "check-in",
      category: t('hotelFaq.categories.checkInOut'),
      question: t('hotelFaq.checkIn.question', { hotelName }),
      answer: checkInHour 
        ? t('hotelFaq.checkIn.answerWithTime', { time: checkInHour, hotelName })
        : t('hotelFaq.checkIn.answerDefault', { hotelName }),
      icon: <Clock className="h-5 w-5" />,
    });

    // Check-out time
    const checkOutHour = rooms[0]?.CheckOutHour;
    items.push({
      id: "check-out",
      category: t('hotelFaq.categories.checkInOut'),
      question: t('hotelFaq.checkOut.question', { hotelName }),
      answer: checkOutHour 
        ? t('hotelFaq.checkOut.answerWithTime', { time: checkOutHour, hotelName })
        : t('hotelFaq.checkOut.answerDefault', { hotelName }),
      icon: <Clock className="h-5 w-5" />,
    });

    // Location
    if (hotel.Address || hotel.City) {
      const fullAddress = [hotel.Address, hotel.City, hotel.Country].filter(Boolean).join(', ');
      items.push({
        id: "location",
        category: t('hotelFaq.categories.location'),
        question: t('hotelFaq.location.question', { hotelName }),
        answer: t('hotelFaq.location.answer', { address: fullAddress, hotelName }),
        icon: <MapPin className="h-5 w-5" />,
      });
    }

    // Board types / Meals
    const uniqueBoardTypes = [...new Set(rooms.map(r => r.BoardDescription).filter(Boolean))];
    if (uniqueBoardTypes.length > 0) {
      const hasBreakfast = uniqueBoardTypes.some(board => {
        const lower = board?.toLowerCase() || '';
        return lower.includes('breakfast') || lower.includes('desayuno') || lower.includes('café') ||
               lower.includes('half board') || lower.includes('full board') || lower.includes('all inclusive') ||
               lower.includes('media pensión') || lower.includes('pensión completa');
      });
      
      items.push({
        id: "meals",
        category: t('hotelFaq.categories.meals'),
        question: t('hotelFaq.meals.question', { hotelName }),
        answer: t('hotelFaq.meals.answer', { options: uniqueBoardTypes.join(', '), hotelName }),
        icon: <UtensilsCrossed className="h-5 w-5" />,
      });

      items.push({
        id: "breakfast",
        category: t('hotelFaq.categories.meals'),
        question: t('hotelFaq.breakfast.question', { hotelName }),
        answer: hasBreakfast 
          ? t('hotelFaq.breakfast.answerYes', { hotelName })
          : t('hotelFaq.breakfast.answerDepends', { hotelName }),
        icon: <UtensilsCrossed className="h-5 w-5" />,
      });
    }

    // Bed types
    const uniqueBedTypes = [...new Set(rooms.map(r => r.BedDescription).filter(Boolean))];
    if (uniqueBedTypes.length > 0) {
      items.push({
        id: "beds",
        category: t('hotelFaq.categories.rooms'),
        question: t('hotelFaq.beds.question', { hotelName }),
        answer: t('hotelFaq.beds.answer', { options: uniqueBedTypes.join(', '), hotelName }),
        icon: <Bed className="h-5 w-5" />,
      });
    }

    // Room options count
    if (rooms.length > 1) {
      items.push({
        id: "room-options",
        category: t('hotelFaq.categories.rooms'),
        question: t('hotelFaq.roomOptions.question', { hotelName }),
        answer: t('hotelFaq.roomOptions.answer', { count: rooms.length, hotelName }),
        icon: <Bed className="h-5 w-5" />,
      });
    }

    // Cancellation policy
    const hasRefundable = rooms.some(r => r.CancellationPolicies && r.CancellationPolicies.length > 0);
    items.push({
      id: "cancellation",
      category: t('hotelFaq.categories.policies'),
      question: t('hotelFaq.cancellation.question', { hotelName }),
      answer: hasRefundable 
        ? t('hotelFaq.cancellation.answerMixed', { hotelName })
        : t('hotelFaq.cancellation.answerNonRefundable', { hotelName }),
      icon: <Shield className="h-5 w-5" />,
    });

    // Pet policy (from manual info)
    if (manualInfo?.is_pet_friendly !== null && manualInfo?.is_pet_friendly !== undefined) {
      items.push({
        id: "pets",
        category: t('hotelFaq.categories.policies'),
        question: t('hotelFaq.pets.question', { hotelName }),
        answer: manualInfo.is_pet_friendly 
          ? t('hotelFaq.pets.answerYes', { hotelName })
          : t('hotelFaq.pets.answerNo', { hotelName }),
        icon: <PawPrint className="h-5 w-5" />,
      });
    }

    // Amenities-based FAQs
    const allAmenities = amenityCategories.flatMap(cat => cat.amenities);
    
    const hasWifi = allAmenities.some(a => 
      a.name.toLowerCase().includes('wifi') || a.name.toLowerCase().includes('internet')
    );
    if (hasWifi) {
      items.push({
        id: "wifi",
        category: t('hotelFaq.categories.amenities'),
        question: t('hotelFaq.wifi.question', { hotelName }),
        answer: t('hotelFaq.wifi.answerYes', { hotelName }),
        icon: <Wifi className="h-5 w-5" />,
      });
    }

    const hasParking = allAmenities.some(a => 
      a.name.toLowerCase().includes('parking') || a.name.toLowerCase().includes('estacionamiento') || a.name.toLowerCase().includes('aparcamiento')
    );
    if (hasParking) {
      items.push({
        id: "parking",
        category: t('hotelFaq.categories.amenities'),
        question: t('hotelFaq.parking.question', { hotelName }),
        answer: t('hotelFaq.parking.answerYes', { hotelName }),
        icon: <Car className="h-5 w-5" />,
      });
    }

    const hasPool = allAmenities.some(a => 
      a.name.toLowerCase().includes('pool') || a.name.toLowerCase().includes('piscina')
    );
    if (hasPool) {
      items.push({
        id: "pool",
        category: t('hotelFaq.categories.amenities'),
        question: t('hotelFaq.pool.question', { hotelName }),
        answer: t('hotelFaq.pool.answerYes', { hotelName }),
        icon: <Waves className="h-5 w-5" />,
      });
    }

    // Contact
    if (hotel.Phone) {
      items.push({
        id: "contact",
        category: t('hotelFaq.categories.contact'),
        question: t('hotelFaq.contact.question', { hotelName }),
        answer: t('hotelFaq.contact.answer', { phone: hotel.Phone, hotelName }),
        icon: <Phone className="h-5 w-5" />,
      });
    }

    return items;
  }, [hotel, rooms, manualInfo, amenityCategories, t, hotelName]);

  // Convert manual FAQ items to FAQItem format
  const manualFaqFormatted = useMemo(() => {
    return manualFaqItems.map(item => ({
      id: `manual-${item.id}`,
      question: getLocalizedQuestion(item),
      answer: getLocalizedAnswer(item),
      icon: <MessageSquare className="h-5 w-5" />,
      category: getCategoryLabel(item.category, t),
      isManual: true,
    }));
  }, [manualFaqItems, getLocalizedQuestion, getLocalizedAnswer, t]);

  // Combine auto and manual FAQ items
  const allFaqItems = useMemo(() => {
    return [...manualFaqFormatted, ...autoFaqItems];
  }, [autoFaqItems, manualFaqFormatted]);

  // Group by category
  const groupedFaqs = useMemo(() => {
    const groups: Record<string, FAQItem[]> = {};
    allFaqItems.forEach(item => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, [allFaqItems]);

  // Notify parent component of FAQ items for Schema.org
  useEffect(() => {
    if (onFaqItemsChange && allFaqItems.length > 0) {
      onFaqItemsChange(allFaqItems.map(item => ({
        question: item.question,
        answer: item.answer
      })));
    }
  }, [allFaqItems, onFaqItemsChange]);

  if (allFaqItems.length === 0) return null;

  return (
    <Collapsible className="mt-8">
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-4 bg-card border rounded-lg hover:bg-accent/50 transition-colors">
          <div className="flex items-center gap-3">
            <HelpCircle className="h-6 w-6 text-primary" />
            <div className="text-left">
              <h2 className="text-xl font-bold">{t('hotelFaq.title')}</h2>
              <p className="text-sm text-muted-foreground">{t('hotelFaq.questionsCount', { count: allFaqItems.length })}</p>
            </div>
          </div>
          <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </div>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="animate-accordion-down">
        <div className="pt-4 space-y-6">
          {Object.entries(groupedFaqs).map(([category, items]) => (
            <div key={category}>
              <h3 className="text-lg font-semibold mb-3 text-primary">{category}</h3>
              <Accordion type="single" collapsible className="space-y-2">
                {items.map((item) => (
                  <AccordionItem 
                    key={item.id} 
                    value={item.id}
                    className="border rounded-lg px-4 bg-card"
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3 text-left">
                        <span className="text-primary">{item.icon}</span>
                        <span className="font-medium">{item.question}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 pt-0">
                      <p className="text-muted-foreground pl-8">{item.answer}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Helper to get category label
function getCategoryLabel(category: string, t: (key: string) => string): string {
  const categoryMap: Record<string, string> = {
    'general': t('hotelFaq.categories.general'),
    'check-in-out': t('hotelFaq.categories.checkInOut'),
    'location': t('hotelFaq.categories.location'),
    'meals': t('hotelFaq.categories.meals'),
    'rooms': t('hotelFaq.categories.rooms'),
    'policies': t('hotelFaq.categories.policies'),
    'amenities': t('hotelFaq.categories.amenities'),
    'services': t('hotelFaq.categories.services'),
    'contact': t('hotelFaq.categories.contact'),
    'dining': t('hotelFaq.categories.dining'),
  };
  return categoryMap[category] || category;
}
